import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SendIcon from '@mui/icons-material/Send';
import { apiFetch } from '../../api/client';
import { withSetorScope, isFiscalSetorial, getFiscalSetor } from '../../utils/dataIsolation';

export default function AlertarAssinaturaPendenteModal({ open, onClose, user, rbacRole, fiscalSetor, unidadeSelecionada, onSuccess }) {
  const scopeOpts = { user, rbacRole, fiscalSetor };
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [pendentes, setPendentes] = useState([]);
  const [selecionados, setSelecionados] = useState({});
  const [mensagem, setMensagem] = useState(
    'Você possui documento(s) aguardando assinatura digital no processo de pagamento de notas fiscais. Por favor, acesse o SGC e conclua a assinatura.'
  );
  const [erro, setErro] = useState('');

  const loadPendentes = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      const setorScope = getFiscalSetor(user, fiscalSetor);
      if (isFiscalSetorial(user, rbacRole) && setorScope) params.set('setor', setorScope);
      const path = withSetorScope(
        `/api/notas-fiscais/fluxo/pendentes-assinatura${params.toString() ? `?${params.toString()}` : ''}`,
        scopeOpts
      );
      const res = await apiFetch(path);
      const data = res.ok ? await res.json() : [];
      const lista = Array.isArray(data) ? data : [];
      setPendentes(lista);
      const init = {};
      lista.forEach((p) => { init[p.chave] = true; });
      setSelecionados(init);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar pendências.');
      setPendentes([]);
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada?.codigo, user, rbacRole, fiscalSetor]);

  useEffect(() => {
    if (open) loadPendentes();
  }, [open, loadPendentes]);

  const selecionadosLista = useMemo(
    () => pendentes.filter((p) => selecionados[p.chave]),
    [pendentes, selecionados]
  );

  const todosSelecionados = pendentes.length > 0 && selecionadosLista.length === pendentes.length;

  const toggleTodos = () => {
    if (todosSelecionados) {
      setSelecionados({});
    } else {
      const all = {};
      pendentes.forEach((p) => { all[p.chave] = true; });
      setSelecionados(all);
    }
  };

  const handleEnviar = async () => {
    const msg = String(mensagem || '').trim();
    if (!msg) {
      setErro('Informe a mensagem do alerta.');
      return;
    }
    if (!selecionadosLista.length) {
      setErro('Selecione ao menos um destinatário.');
      return;
    }
    setEnviando(true);
    setErro('');
    try {
      const res = await apiFetch('/api/notas-fiscais/fluxo/alertar-assinatura', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mensagem: msg,
          itens: selecionadosLista.map((p) => ({ chave: p.chave })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Erro ao enviar alertas.');
      onSuccess?.(data);
      onClose?.();
    } catch (e) {
      setErro(e.message || 'Erro ao enviar alertas.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <NotificationsActiveIcon color="error" />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Alertar pendências de assinatura
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Envia alerta no sistema (sininho) e e-mail para os selecionados
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        <TextField
          fullWidth
          required
          multiline
          minRows={3}
          label="Mensagem do alerta"
          placeholder="Descreva o que o signatário deve fazer"
          value={mensagem}
          onChange={(e) => setMensagem(e.target.value)}
          helperText="Esta mensagem aparece no sininho do sistema e no corpo do e-mail"
          sx={{ mb: 2 }}
        />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Destinatários pendentes ({pendentes.length})
          </Typography>
          {pendentes.length > 0 && (
            <Button size="small" onClick={toggleTodos}>
              {todosSelecionados ? 'Desmarcar todos' : 'Selecionar todos'}
            </Button>
          )}
        </Stack>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : pendentes.length === 0 ? (
          <Alert severity="info">Nenhum signatário pendente no fluxo de pagamento no momento.</Alert>
        ) : (
          <Stack spacing={0.5} sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {pendentes.map((p) => (
              <Box key={p.chave}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!selecionados[p.chave]}
                      onChange={(e) => setSelecionados((prev) => ({ ...prev, [p.chave]: e.target.checked }))}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {p.participanteNome || p.participanteEmail} — {p.numeroProcesso}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.participanteEmail} · {p.empresa || '—'} · {p.fase}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0, width: '100%' }}
                />
                <Divider />
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button
          variant="contained"
          color="error"
          startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={enviando || loading || !selecionadosLista.length || !String(mensagem || '').trim()}
          onClick={handleEnviar}
        >
          Enviar alerta ({selecionadosLista.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
